import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Switch, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { useCollectionClient } from '../hooks/useCollectionClient';
import { useCollectionClientNotes } from '../hooks/useCollectionClientNotes';
import { useUpdateCollectionClient } from '../hooks/useUpdateCollectionClient';
import { useUpdateCollectionClientPermanentStatus } from '../hooks/useUpdateCollectionClientPermanentStatus';
import { CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';
import { hasMultiDepartmentInvoice } from '../utils/groupInvoices';
import ClientInvoicesTable from '../components/ClientInvoicesTable';
import ClientEmailHistoryTable from '../components/ClientEmailHistoryTable';
import CollectionClientNotesModal from '../components/CollectionClientNotesModal';
import PermanentOffReasonModal from '../components/PermanentOffReasonModal';
import { SectionLabel } from '../components/clientDetailPresentation';
import ClientCompanyInfoCard from '../components/ClientCompanyInfoCard';
import ClientRecentNotesCard from '../components/ClientRecentNotesCard';
import { useCollectionsPermissions } from '../../shared/useCollectionsPermissions';
import { permanentOffTooltip, ADMIN_ONLY_TOGGLE_TOOLTIP, ADMIN_ONLY_PERMANENT_TOOLTIP } from '../utils/format';
import {
  NO_BALANCE_OWED_TOGGLE_TOOLTIP,
  RESIDENTIAL_SUPPRESSED_TOGGLE_TOOLTIP,
  NO_USABLE_EMAIL_TOGGLE_TOOLTIP,
  bucketDisabledToggleTooltip,
} from '../components/CollectionClientsTableTooltip';

const CollectionClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const clientId = Number(id);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const { data, isLoading, isError } = useCollectionClient(clientId);
  const client = data?.data ?? null;
  const { data: notesData } = useCollectionClientNotes(clientId);
  const notes = notesData?.data ?? [];
  const { updateClient, isLoading: isToggling } = useUpdateCollectionClient();
  const { updatePermanentStatus, isLoading: isPermanentSaving } = useUpdateCollectionClientPermanentStatus();
  const { isCollectionsAdmin } = useCollectionsPermissions();

  const [collectionEnabled, setCollectionEnabled] = useState(false);
  const [permanentlyOff, setPermanentlyOff] = useState(false);
  const [permanentAudit, setPermanentAudit] = useState<{
    by: string | null;
    at: string | null;
    reason: string | null;
  }>({ by: null, at: null, reason: null });
  const [permanentModalOpen, setPermanentModalOpen] = useState(false);
  const [documents, setDocuments] = useState<CollectionInvoiceDocument[]>([]);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    if (client) {
      setCollectionEnabled(client.collection_enabled);
      setPermanentlyOff(client.collection_permanently_disabled);
      setPermanentAudit({
        by: client.collection_permanently_disabled_by,
        at: client.collection_permanently_disabled_at,
        reason: client.collection_permanently_disabled_reason,
      });
      setDocuments(client.invoice_documents);
    }
  }, [client]);

  const handleToggleCollectionEnabled = async (checked: boolean) => {
    if (!client) return;
    const previous = collectionEnabled;
    setCollectionEnabled(checked);
    try {
      await updateClient({ id: clientId, collectionEnabled: checked });
      showSuccessSnackBar?.(`Client ${checked ? 'enabled' : 'disabled'} for collection emails.`);
    } catch (error: any) {
      setCollectionEnabled(previous);
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update client status.');
    }
  };

  const submitPermanent = async (permanentlyDisabled: boolean, reason?: string) => {
    const previousOff = permanentlyOff;
    const previousAudit = permanentAudit;
    // Keep tooltip accurate immediately; overwrite with server audit fields once the PUT returns.
    setPermanentlyOff(permanentlyDisabled);
    setPermanentAudit(
      permanentlyDisabled
        ? { by: null, at: new Date().toISOString(), reason: reason ?? null }
        : { by: null, at: null, reason: null },
    );

    try {
      const res = await updatePermanentStatus({ id: clientId, permanentlyDisabled, reason });
      setPermanentlyOff(res.data.collection_permanently_disabled);
      setPermanentAudit({
        by: res.data.collection_permanently_disabled_by,
        at: res.data.collection_permanently_disabled_at,
        reason: res.data.collection_permanently_disabled_reason,
      });
      void queryClient.invalidateQueries({ queryKey: [`/collections/clients/${clientId}`, undefined] });
      showSuccessSnackBar?.(permanentlyDisabled ? 'Collections permanently disabled.' : 'Collections reactivated.');
    } catch (error: any) {
      setPermanentlyOff(previousOff);
      setPermanentAudit(previousAudit);
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update permanent status.');
    }
  };

  // OFF just needs a confirm; ON requires a recorded reason, so it opens the modal and the actual
  // mutation fires from the modal's onConfirm (mirrors CollectionClientsListPage).
  const handlePermanentToggle = (checked: boolean) => {
    if (checked) {
      setPermanentModalOpen(true);
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm('Reactivate this client? Collection emails can be turned on again after this.')) return;
    void submitPermanent(false);
  };

  const handleUploaded = (applyTo: string, pdf: CollectionInvoicePdf) => {
    setDocuments((prev) => prev.map((d) => (d.apply_to === applyTo ? { ...d, has_pdf: true, pdf } : d)));
    showSuccessSnackBar?.('Invoice PDF uploaded.');
  };

  const handleDeleted = (applyTo: string) => {
    setDocuments((prev) => prev.map((d) => (d.apply_to === applyTo ? { ...d, has_pdf: false, pdf: null } : d)));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !client) {
    return (
      <Grid container>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="error">Failed to load client.</Alert>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/collections/clients')}>
            Back to Clients
          </Button>
        </Grid>
      </Grid>
    );
  }

  const toggleBlocked = !client.toggle_allowed;
  const recipientEmails = client.emails ?? [];

  const permanentOffAuditTooltip = permanentOffTooltip(permanentAudit.by, permanentAudit.at, permanentAudit.reason);
  let collectionEmailsTooltip = '';
  if (permanentlyOff) collectionEmailsTooltip = permanentOffAuditTooltip;
  else if (client.suppression_reason === 'no_balance_owed') collectionEmailsTooltip = NO_BALANCE_OWED_TOGGLE_TOOLTIP;
  else if (client.suppression_reason === 'residential') collectionEmailsTooltip = RESIDENTIAL_SUPPRESSED_TOGGLE_TOOLTIP;
  else if (client.suppression_reason === 'bucket_disabled') {
    // bucket_disabled reflects overall tone; detail is unscoped but overall_tone is set when present.
    collectionEmailsTooltip = bucketDisabledToggleTooltip(client.overall_tone ?? client.tone);
  } else if (!isCollectionsAdmin) collectionEmailsTooltip = ADMIN_ONLY_TOGGLE_TOOLTIP;
  else if (toggleBlocked) collectionEmailsTooltip = NO_USABLE_EMAIL_TOGGLE_TOOLTIP;
  const permanentOffToggleTooltip = permanentlyOff
    ? permanentOffAuditTooltip
    : !isCollectionsAdmin
      ? ADMIN_ONLY_PERMANENT_TOOLTIP
      : '';

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle={`Collections — ${client.company}`} titleIcon={<BusinessIcon />} />
      </Grid>

      <Grid
        item
        xs={12}
        sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
      >
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => history.push('/collections/clients')}>
          Back to Clients
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {client.tone && <Chip label={client.tone} size="small" />}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">Collection Emails</Typography>
            <Tooltip title={collectionEmailsTooltip} disableHoverListener={!collectionEmailsTooltip}>
              <span>
                <Switch
                  checked={toggleBlocked || permanentlyOff ? false : collectionEnabled}
                  onChange={(e) => handleToggleCollectionEnabled(e.target.checked)}
                  color="primary"
                  size="small"
                  disabled={isToggling || toggleBlocked || !isCollectionsAdmin || permanentlyOff}
                  inputProps={{ 'aria-label': `Collection emails for ${client.company}` }}
                />
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">Permanent Off</Typography>
            <Tooltip title={permanentOffToggleTooltip} disableHoverListener={!permanentOffToggleTooltip}>
              <span>
                <Switch
                  checked={permanentlyOff}
                  onChange={(e) => handlePermanentToggle(e.target.checked)}
                  color="error"
                  size="small"
                  disabled={!isCollectionsAdmin || isPermanentSaving}
                  inputProps={{ 'aria-label': `Permanently disable collections for ${client.company}` }}
                />
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '65fr 35fr' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <ClientCompanyInfoCard
            client={client}
            clientId={clientId}
            invoiceDocuments={documents}
            recipientEmails={recipientEmails}
            contactsOverride={client.contacts.filter((c) => !/statement/i.test(c.name ?? ''))}
            onEmailSent={() => {
              void queryClient.invalidateQueries({ queryKey: [`/collections/clients/${clientId}/notes`, undefined] });
              void queryClient.invalidateQueries({
                predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === '/collections/email_sends',
              });
            }}
          />

          <ClientRecentNotesCard notes={notes} onViewAll={() => setNotesOpen(true)} />
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <SectionLabel>Aging Invoices</SectionLabel>
        {hasMultiDepartmentInvoice(documents) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Invoices spanning multiple departments are listed under each. Department totals may exceed the client&apos;s
            total past due.
          </Typography>
        )}
        <ClientInvoicesTable
          documents={documents}
          location={client.location}
          acctNo={client.acct_no}
          onUploaded={handleUploaded}
          onDeleted={handleDeleted}
        />
      </Grid>

      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <SectionLabel>Email History</SectionLabel>
        <ClientEmailHistoryTable location={client.location} clientId={clientId} />
      </Grid>

      <CollectionClientNotesModal
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        clientId={clientId}
        company={client.company}
        onNotesChanged={() => {
          void queryClient.invalidateQueries({ queryKey: [`/collections/clients/${clientId}/notes`, undefined] });
        }}
      />

      <PermanentOffReasonModal
        open={permanentModalOpen}
        company={client.company}
        isSubmitting={isPermanentSaving}
        onClose={() => setPermanentModalOpen(false)}
        onConfirm={(reason) => {
          setPermanentModalOpen(false);
          void submitPermanent(true, reason);
        }}
      />
    </Grid>
  );
};

export default CollectionClientDetailPage;
